import { cn } from '@/lib/utils';

/**
 * Where a monthly payment actually goes, as one bar.
 *
 * WHY A BAR AND NOT THE LIST OF NUMBERS IT REPLACES
 *
 * The calculator already computed principal-and-interest, tax, insurance and
 * HOA, and rendered them as four `flex justify-between` rows — which answers
 * "how much is tax" and cannot answer "how much of this is tax", the question a
 * first-time buyer is actually asking. A proportion is the one thing a list of
 * dollar figures makes you do arithmetic for, and it is exactly what a stacked
 * bar shows without any.
 *
 * The insight it makes visible is genuinely useful and slightly alarming: at
 * California's ~1.2% property tax on a $1.2M home, tax and insurance together
 * are roughly a quarter of the monthly cost. Nobody reads that out of four
 * right-aligned numbers.
 *
 * NO CHART LIBRARY. Four divs in a flex row, widths in percent. The site ships
 * no charting dependency and holds a CI bundle budget; this is the whole
 * implementation.
 *
 * ACCESSIBILITY. The bar is decorative and hidden, because the `<dl>` beneath it
 * lists every component with its exact figure — a screen-reader user gets the
 * precise numbers rather than a described approximation of a picture. The legend
 * IS exposed, since it names the segments and carries the percentages, which is
 * the one thing the list below does not say.
 */

export interface PaymentPart {
  label: string;
  amount: number;
  /** Tailwind background class from the design tokens — never a raw colour. */
  className: string;
}

export default function PaymentSplitBar({ parts }: { parts: PaymentPart[] }) {
  const shown = parts.filter((p) => p.amount > 0);
  const total = shown.reduce((sum, p) => sum + p.amount, 0);
  // A payment of nothing has no split, and dividing by it would render NaN%
  // widths — which browsers quietly drop, leaving an empty track that looks
  // like a loading state that never resolves.
  if (total <= 0 || shown.length === 0) return null;

  const withPct = shown.map((p) => ({ ...p, pct: (p.amount / total) * 100 }));

  return (
    <div className="mt-5">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-glass"
        aria-hidden="true"
      >
        {withPct.map((p) => (
          <div
            key={p.label}
            className={cn('h-full', p.className)}
            // A 1.5% segment (a small HOA) is otherwise a sub-pixel sliver that
            // renders as nothing while its legend entry insists it is there.
            style={{ width: `${Math.max(p.pct, 1.5)}%` }}
          />
        ))}
      </div>

      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-muted">
        {withPct.map((p) => (
          <li key={p.label} className="flex items-center gap-1.5">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', p.className)} aria-hidden />
            {p.label}
            <span className="tabular-nums text-ink-soft">{Math.round(p.pct)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
